import { Modal as MantineModal, rem, ScrollArea } from "@mantine/core";
import { useToggle } from "@mantine/hooks";
import type { CustomModalBodyFactory, CustomModalNewBodyFactory, IProps } from "./modal-body.types";
import { componentWrapper } from "../../../utilities";
import { BoxProps } from "../../box";
import styles from "./modal-body.module.scss";
import modalStyles from "../modal.module.scss";
import { Group } from "../../group";
import { Stack, StackProps } from "../../stack";
import { ActionIcon } from "../../action-icon";
import { Icon } from "../../icon";
import { faSidebar } from "@fortawesome/pro-solid-svg-icons";
import classnames from "classnames";

const ModalBody = componentWrapper<CustomModalBodyFactory, IProps>("ModalBody", (props, ref) => {
    const { children, ...otherProps } = props;
    return (
        <MantineModal.Body classNames={{ body: modalStyles["body"] }} ref={ref} {...otherProps}>
            <ScrollArea.Autosize mx="auto" mah="70vh" w="100%">
                {children}
            </ScrollArea.Autosize>
        </MantineModal.Body>
    );
});

const ModalBodyContent = ({ className, ...otherProps }: StackProps) => {
    return (
        <Stack
            gap="0"
            flex={1}
            h="100%"
            className={classnames(styles["body-content"], className)}
            pos="relative"
            {...otherProps}
        />
    );
};

const ModalBodySidebar = ({
    children,
    showToggleIcon = true,
    ...otherProps
}: Omit<BoxProps, "children"> & {
    children: React.ReactNode | ((isCollapsed: boolean) => React.ReactNode);
    showToggleIcon?: boolean;
}) => {
    const [value, toggle] = useToggle([
        { width: rem(200), isCollapsed: false },
        { width: rem(64), isCollapsed: true },
    ]);
    return (
        <Stack
            data-is-sidebar-collapsed={value.isCollapsed}
            w={value.width}
            p="sm"
            pr={"0"}
            h="100%"
            className={styles["body-sidebar"]}
            {...otherProps}
        >
            <ScrollArea offsetScrollbars={"y"} scrollbars="y" className={styles["body-sidebar-content"]}>
                {typeof children === "function" ? children(value.isCollapsed) : children}
            </ScrollArea>
            {showToggleIcon && (
                <Group px="sm">
                    <ActionIcon onClick={() => toggle()} variant="subtle" color="gray" size="sm">
                        <Icon icon={faSidebar} />
                    </ActionIcon>
                </Group>
            )}
        </Stack>
    );
};

const ModalBodyWithSidebar = componentWrapper<CustomModalNewBodyFactory, IProps>(
    "ModalBody",
    ({ children, ...otherProps }, ref) => {
        return (
            <MantineModal.Body flex={1} classNames={{ body: styles["body"] }} ref={ref} {...otherProps}>
                <Group gap="0" flex={1} align="flex-start" wrap={"nowrap"}>
                    {children}
                </Group>
            </MantineModal.Body>
        );
    }
);

ModalBodyWithSidebar.Content = ModalBodyContent;
ModalBodyWithSidebar.Sidebar = ModalBodySidebar;

export { ModalBody, ModalBodyWithSidebar, ModalBodyContent, ModalBodySidebar };
